<template>
  <div class="flex-container">
    <!-- 顶部 header -->
    <div class="flex-header" v-loading="loading.header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/sys/merchant/merchantList' }">商户管理</el-breadcrumb-item>
        <el-breadcrumb-item>{{ merchantName }}</el-breadcrumb-item>
      </el-breadcrumb>
      <div class="merchant-header" v-if="workflow.id">
        <!-- 标题行：商户名称 + 信用代码 + 状态标签 -->
        <h2 class="merchant-title">
          {{ merchantName || workflow.channelInfo && workflow.channelInfo.channelName }}
          <span class="merchant-code" v-if="merchantCreditCode">（{{ merchantCreditCode }}）</span>
          <el-tag v-if="workflow.wfType" type="info" size="small" style="margin-left:6px;">
            {{ wfTypeMap[workflow.wfType] || '-' }}
          </el-tag>
        </h2>
        <!-- 信息行：渠道、流程编号、创建时间 -->
        <div class="info-card__meta">
          <div class="meta-item">
            <span class="meta-label">所属渠道</span>
            <span class="meta-value">{{ workflow && workflow.channelName || '-' }}</span>
          </div>
          <div class="meta-divider" />
          <div class="meta-item">
            <span class="meta-label">流程编号</span>
            <span class="meta-value">{{ workflow.id || '-' }}</span>
          </div>
          <div class="meta-divider" />
          <div class="meta-item">
            <span class="meta-label">流程时间</span>
            <span class="meta-value">{{ workflow.createTime || '-' }}</span>
          </div>
          <div class="meta-spacer" />
          <el-button type="primary" size="small" icon="el-icon-back" @click="goBack">返回列表</el-button>
        </div>
      </div>
    </div>

    <!-- 主体内容：Tab 面板 -->
    <div class="flex-item main-content">
      <el-tabs v-model="activeTab" class="detail-tabs" @tab-click="onTabClick">
        <!-- Tab 1：关联文件 -->
        <el-tab-pane label="关联文件" name="files">
          <div class="tree-container" v-loading="loading.files">
            <el-tree
              v-if="qualificationFiles.length > 0"
              :data="qualificationFiles"
              :props="{ children: 'children', label: 'label' }"
              default-expand-all
              node-key="id"
              highlight-current
              style="width: 100%; padding: 8px 0;"
            >
              <span slot-scope="{ node, data }" class="custom-tree-node">
                <span class="tree-node-label" :title="data.label">
                  <i :class="data.type === 'folder' ? 'el-icon-folder' : getFileIcon(data.extension)" class="tree-node-icon"></i>
                  {{ data.label }}
                </span>
                <span class="tree-node-actions" v-if="data.type === 'file'">
                  <el-button type="text" size="mini" @click.stop="previewFile(data)" v-if="data.extension === 'pdf'">预览</el-button>
                  <el-button type="text" size="mini" @click.stop="downloadFile(data)">下载</el-button>
                </span>
              </span>
            </el-tree>
            <el-empty v-else description="暂无关联文件" />
          </div>
        </el-tab-pane>

        <!-- Tab 2：上传文件 -->
        <el-tab-pane label="上传文件" name="upload">
          <div class="file-table-wrapper" v-loading="loading.upload">
            <el-table
                v-if="uploadRecordList.length > 0"
                :data="uploadRecordList"
                border
                size="small"
                style="width:100%;"
                :height="tableHeight"
            >
              <el-table-column type="index" label="序号" width="60" align="center" />
              <el-table-column prop="fileName" label="文件名称" min-width="200" show-overflow-tooltip />
              <el-table-column prop="uploadDate" label="上传时间" width="170" align="center" />
              <el-table-column label="操作" width="100" align="center" fixed="right">
                <template slot-scope="scope">
                  <el-button type="text" size="small" @click="downloadFile(scope.row)">下载</el-button>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-else description="暂无上传记录" />
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import { detail, getQualificationFiles, getUploadFileList } from '@/api/system/workflow/workflow';
import { SYS_APi_SCREENING_URL } from '@/utils/globals';
import { streamDownload } from '@/utils/download';

const wfTypeMap = {
  '1': '渠道遴选',
  '2': '新增商户/门店',
  '4': '修改商户/门店',
  '3': '退出商户/门店',
  '6': '支付业务许可证'
};

export default {
  name: "merchantView",
  data() {
    return {
      flowId: '',
      merchantName: '',
      merchantCreditCode: '',
      listSearchForm: {},
      activeTab: 'files',
      tabLoaded: {},
      workflow: {},
      uploadRecordList: [],
      qualificationFiles: [],
      loading: { header: true, upload: false, files: false },
      tableHeight: 400
    };
  },
  created() {
    this.flowId = this.$route.params.flowId || '';
    this.merchantName = this.$route.query.merchantName || '';
    this.merchantCreditCode = this.$route.query.merchantCreditCode || '';
    this.listSearchForm = {
      keyword: this.$route.query.keyword || '',
      channelId: this.$route.query.channelId || '',
      wfType: this.$route.query.wfType || '',
      status: this.$route.query.status || '',
      timeField: this.$route.query.timeField || '',
      startTime: this.$route.query.startTime || '',
      endTime: this.$route.query.endTime || '',
      currentPage: this.$route.query.currentPage || 1,
      pageSize: this.$route.query.pageSize || 10
    };
    if (!this.flowId) {
      this.$message.error('流程ID不能为空');
      this.goBack();
      return;
    }
    this.loadHeader();
    this.loadTabData('files', this.flowId);
  },
  mounted() {
    this.calcTableHeight();
    window.addEventListener('resize', this.calcTableHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableHeight);
  },
  computed: {
    wfTypeMap() { return wfTypeMap; }
  },
  methods: {
    loadHeader() {
      this.loading.header = true;
      detail({ workflowId: this.flowId }).then(res => {
        if (res && res.code === '200') {
          this.workflow = res.data || {};
        } else {
          this.$message.error(res && res.message ? res.message : '加载流程信息失败');
        }
      }).catch(() => {
        this.$message.error('加载流程信息失败，请稍后重试');
      }).finally(() => {
        this.loading.header = false;
      });
    },

    onTabClick(tab) {
      const tabName = tab.paneName;
      if (!tabName) return;
      this.loadTabData(tabName, this.flowId);
    },

    loadTabData(tabName, flowId) {
      if (this.tabLoaded[tabName]) return;
      this.tabLoaded[tabName] = true;

      const loaders = {
        upload: () => {
          this.loading.upload = true;
          getUploadFileList({ workflowId: flowId }).then(res => {
            if (res && res.code === '200') this.uploadRecordList = res.data || [];
            else this.uploadRecordList = [];
          }).catch(() => { this.uploadRecordList = []; })
            .finally(() => { this.loading.upload = false; });
        },
        files: () => {
          this.loading.files = true;
          getQualificationFiles({ workflowId: flowId }).then(res => {
            if (res && res.code === '200') this.qualificationFiles = res.data || [];
            else this.qualificationFiles = [];
          }).catch(() => { this.qualificationFiles = []; })
            .finally(() => { this.loading.files = false; });
        }
      };

      if (loaders[tabName]) loaders[tabName]();
    },

    getFileIcon(ext) {
      const map = { pdf: 'el-icon-document', xlsx: 'el-icon-tickets', xls: 'el-icon-tickets', doc: 'el-icon-document', docx: 'el-icon-document', zip: 'el-icon-folder-opened', rar: 'el-icon-folder-opened' };
      return map[ext] || 'el-icon-document';
    },

    wfStateTagType(state) {
      if (['6', '4,6'].includes(state)) return 'success';
      if (['4', '7'].includes(state)) return 'danger';
      if (['1', '1-0', '1-1', '1-2', '3'].includes(state)) return 'warning';
      return 'info';
    },

    wfStateText(state) {
      if (['6'].includes(state)) return '通过';
      if (['4,6'].includes(state)) return '部分通过';
      if (['4', '7'].includes(state)) return '不通过';
      if (['1', '1-0', '1-1', '1-2', '3'].includes(state)) return '审核中';
      if (state === '0') return '待提交';
      return '-';
    },

    previewFile(file) {
      if (!file.path) return;
      const loading = this.$loading({
        lock: true, text: '加载文件中...', spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      const token = this.$store.state.token;
      if (!token) { this.$message.error('未登录，无法预览'); loading.close(); return; }
      const apiUrl = SYS_APi_SCREENING_URL + '/filedown/bigFileDownLoad';
      const params = new URLSearchParams();
      params.append('url', file.path);
      params.append('name', file.label);
      const finalUrl = `${apiUrl}?${params.toString()}`;
      fetch(finalUrl, { headers: { 'token': token } })
        .then(res => { if (!res.ok) throw new Error(`服务器错误: ${res.status}`); return res.blob(); })
        .then(blob => {
          loading.close();
          const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
          window.open(blobUrl, '_blank');
        })
        .catch(() => { loading.close(); this.$message.error('预览失败'); });
    },

    downloadFile(row) {
      const loading = this.$loading({
        lock: true, text: '正在下载...', spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      const apiUrl = SYS_APi_SCREENING_URL + '/filedown/bigFileDownLoad';
      const params = new URLSearchParams();
      params.append('url', row.path || row.filePath || '');
      params.append('name', row.label || row.fileName || 'download');
      const finalUrl = `${apiUrl}?${params.toString()}`;
      const token = this.$store.state.token;
      if (!token) { this.$message.error('未登录，无法下载'); loading.close(); return; }
      streamDownload(finalUrl, {
          token,
          filename: row.label || row.fileName || 'download',
          onProgress: (percent, downloadedMB) => {
            if (!percent) {
              loading.setText('正在下载...');
            } else if (downloadedMB < 1) {
              loading.setText(`正在下载 ${percent}% (${(downloadedMB * 1024).toFixed(0)} KB)...`);
            } else {
              loading.setText(`正在下载 ${percent}% (${downloadedMB.toFixed(1)} MB)...`);
            }
          },
        })
        .then(() => { this.$message.success('下载成功'); })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error(err);
            this.$message.error(err.message || '下载失败');
          }
        })
        .finally(() => { loading.close(); });
    },

    calcTableHeight() {
      this.$nextTick(() => {
        if (typeof window === 'undefined') return;
        const clientHeight = window.innerHeight || document.documentElement.clientHeight;
        this.tableHeight = Math.max(200, clientHeight - 280);
      });
    },

    goBack() { this.$router.push({ path: '/sys/merchant/merchantList', query: this.listSearchForm }); }
  }
};
</script>

<style scoped>
.flex-container { height: 100%; display: flex; flex-direction: column; text-align: left; }
.flex-header { flex-shrink: 0; background: #fff; padding-bottom: 8px; }
.flex-item:nth-child(2) { flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden; margin-top: 12px; }
.main-content { flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }

.merchant-header { padding: 14px 20px; background: #dce4f0; border-radius: 8px; margin-top: 20px; border: 1px solid #ebeef5; }
.merchant-title { font-size: 18px; font-weight: 600; color: #303133; margin: 0; }
.merchant-code { font-size: 14px; font-weight: 400; color: #909399; }

/* Tab 容器 */
.detail-tabs { flex: 1; min-height: 0; display: flex; flex-direction: column; background: #fff; border-radius: 8px; box-shadow: 0 1px 4px rgba(0, 21, 41, 0.06); padding: 0 16px; }
.detail-tabs >>> .el-tabs__header { background: #fff; margin-bottom: 0; flex-shrink: 0; border-bottom: 1px solid #ebeef5; }
.detail-tabs >>> .el-tabs__content { flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; padding: 16px 0; padding-right: 6px; display: flex; flex-direction: column; }
.detail-tabs >>> .el-tab-pane { flex: 1; min-height: 0; display: flex; flex-direction: column; }

/* 上传文件表格 */
.file-table-wrapper { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.file-table-wrapper >>> .el-table { flex: 1; min-height: 0; }

/* 文件树容器 */
.tree-container { width: 100%; height: 100%; overflow: auto; padding-bottom: 8px; }

.custom-tree-node { flex: 1; display: flex; align-items: center; justify-content: space-between; font-size: 14px; padding-right: 8px; min-width: 0; }
.tree-node-label { display: flex; align-items: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; min-width: 0; }
.tree-node-icon { margin-right: 6px; font-size: 16px; color: #606266; }
.tree-node-actions { display: flex; align-items: center; margin-left: 12px; flex-shrink: 0; }
.tree-node-actions .el-button { padding: 0; margin-left: 8px; }

/* 滚动条 */
.file-table-wrapper >>> .el-table__body-wrapper::-webkit-scrollbar,
.tree-container::-webkit-scrollbar { width: 8px; height: 8px; }
.file-table-wrapper >>> .el-table__body-wrapper::-webkit-scrollbar-track,
.tree-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
.file-table-wrapper >>> .el-table__body-wrapper::-webkit-scrollbar-thumb,
.tree-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.file-table-wrapper >>> .el-table__body-wrapper::-webkit-scrollbar-thumb:hover,
.tree-container::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }

/* 信息行 */
.info-card__meta { display: flex; align-items: center; gap: 0; flex-wrap: nowrap; overflow: hidden; margin-top: 10px; }
.meta-item { display: flex; align-items: center; gap: 6px; flex-shrink: 0; min-width: 0; }
.meta-label { font-size: 12px; color: #909399; white-space: nowrap; flex-shrink: 0; }
.meta-value { font-size: 13px; color: #606266; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex: 1; }
.meta-divider { width: 1px; height: 14px; background: #1557da; flex-shrink: 0; margin: 0 12px; }
.meta-spacer { flex: 1; min-width: 8px; }
</style>
